/**
 * DOCX Raw Text Extractor
 *
 * Loads Mammoth only when a DOCX file is actually processed.
 *
 * Primary responsibilities:
 * - Extract plain text from browser DOCX ArrayBuffers
 * - Keep Mammoth out of initial client layout bundles
 *
 * Dependencies:
 * - mammoth
 */

type MammothApi = {
  extractRawText: (input: { arrayBuffer: ArrayBuffer }) => Promise<{
    value: string
  }>
}

const isMammothApi = (value: unknown): value is MammothApi => {
  return (
    typeof value === 'object' &&
    value !== null &&
    'extractRawText' in value &&
    typeof value.extractRawText === 'function'
  )
}

const resolveMammothApi = (module: unknown): MammothApi => {
  if (isMammothApi(module)) {
    return module
  }

  if (
    typeof module === 'object' &&
    module !== null &&
    'default' in module &&
    isMammothApi(module.default)
  ) {
    return module.default
  }

  throw new Error('DOCX parser failed to load')
}

export const extractDocxRawText = async (arrayBuffer: ArrayBuffer): Promise<string> => {
  const mammoth = resolveMammothApi(await import('mammoth'))
  const result = await mammoth.extractRawText({ arrayBuffer })

  if (!result.value) {
    throw new Error('No text content found in DOCX file')
  }

  return result.value
}
